// Zuschlagsrelevante Stunden und Prüfungen nach dem Arbeitszeitgesetz.
//
// Die Zeitfenster folgen der steuerlichen Praxis (§3b EStG): Nachtarbeit 20–6 Uhr,
// Sonntag und gesetzlicher Feiertag jeweils 0–24 Uhr. Das ist NICHT identisch mit
// der arbeitsschutzrechtlichen Definition (§2 ArbZG: 23–6 Uhr) — für die Lohnabrechnung
// zählt die steuerliche.
//
// Wichtig: Ein Zeitabschnitt kann über Mitternacht laufen und mehrere Kategorien
// berühren. Deshalb wird minutenweise in Stundenblöcke zerlegt statt pauschal gerechnet.
import { dauerMinuten, type Stempel } from './berechnung';
import { istFeiertag } from './feiertage';

export interface Zuschlagsstunden {
  nacht: number;
  sonntag: number;
  feiertag: number;
  gesamt: number;
}

const NACHT_AB = 20; // 20:00
const NACHT_BIS = 6; // 06:00

const tagKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Zerlegt einen Abschnitt in Minuten und ordnet sie zu. Eine Minute kann in mehreren
 * Kategorien liegen (Sonntagnacht); die Summen sind deshalb NICHT überschneidungsfrei.
 * Die Lohnabrechnung wendet die Zuschläge ohnehin nach eigenen Vorrangregeln an.
 */
export function zuschlagsMinuten(beginn: string, ende: string | null, jetzt: Date = new Date()): Zuschlagsstunden {
  const start = new Date(beginn);
  const stop = ende ? new Date(ende) : jetzt;
  const raus: Zuschlagsstunden = { nacht: 0, sonntag: 0, feiertag: 0, gesamt: 0 };
  if (Number.isNaN(start.getTime()) || Number.isNaN(stop.getTime()) || stop <= start) return raus;

  // In 5-Minuten-Schritten abtasten: fein genug für Zuschläge, schnell genug für
  // ganze Monate (ein 8-Stunden-Tag sind 96 Schritte).
  const SCHRITT = 5;
  for (let t = start.getTime(); t < stop.getTime(); t += SCHRITT * 60000) {
    const d = new Date(t);
    const rest = Math.min(SCHRITT, (stop.getTime() - t) / 60000);
    const stunde = d.getHours();
    raus.gesamt += rest;
    if (stunde >= NACHT_AB || stunde < NACHT_BIS) raus.nacht += rest;
    if (istFeiertag(tagKey(d))) raus.feiertag += rest;
    else if (d.getDay() === 0) raus.sonntag += rest; // Feiertag hat Vorrang
  }
  return {
    nacht: Math.round(raus.nacht),
    sonntag: Math.round(raus.sonntag),
    feiertag: Math.round(raus.feiertag),
    gesamt: Math.round(raus.gesamt),
  };
}

/** Summe über mehrere Abschnitte. */
export function zuschlaegeSumme(stempel: Stempel[], jetzt: Date = new Date()): Zuschlagsstunden {
  return stempel.reduce<Zuschlagsstunden>((s, x) => {
    const z = zuschlagsMinuten(x.beginn, x.ende, jetzt);
    return {
      nacht: s.nacht + z.nacht,
      sonntag: s.sonntag + z.sonntag,
      feiertag: s.feiertag + z.feiertag,
      gesamt: s.gesamt + z.gesamt,
    };
  }, { nacht: 0, sonntag: 0, feiertag: 0, gesamt: 0 });
}

// ── Arbeitszeitgesetz ────────────────────────────────────────────────────────

export type VerstossArt = 'hoechstarbeitszeit' | 'ruhezeit' | 'pause' | 'sonntag';

export interface Verstoss {
  art: VerstossArt;
  tag: string;
  text: string;
}

const HOECHST_MINUTEN = 10 * 60;   // §3 ArbZG: max. 10 Stunden werktäglich
const RUHEZEIT_MINUTEN = 11 * 60;  // §5 ArbZG: 11 Stunden zwischen zwei Arbeitstagen

/**
 * Prüft die harten Grenzen des Arbeitszeitgesetzes. Bewusst als HINWEIS gedacht,
 * nicht als Sperre: die Zeiten sollen ehrlich erfasst werden, auch wenn ein Tag
 * zu lang war — die Konsequenz zieht der Mensch, nicht die Software.
 */
export function pruefeArbeitszeitgesetz(stempel: Stempel[], jetzt: Date = new Date()): Verstoss[] {
  const raus: Verstoss[] = [];
  const nachTag = new Map<string, Stempel[]>();
  for (const s of stempel) {
    const t = tagKey(new Date(s.beginn));
    nachTag.set(t, [...(nachTag.get(t) || []), s]);
  }

  for (const [tag, liste] of [...nachTag.entries()].sort()) {
    const brutto = liste.reduce((sum, s) => sum + dauerMinuten(s.beginn, s.ende, jetzt), 0);
    const pause = liste.reduce(
      (sum, s) => sum + (s.pausen || []).reduce((p, x) => p + dauerMinuten(x.beginn, x.ende, jetzt), 0),
      0,
    );
    const arbeit = brutto - pause;

    if (arbeit > HOECHST_MINUTEN) {
      raus.push({
        art: 'hoechstarbeitszeit', tag,
        text: `${(arbeit / 60).toFixed(1).replace('.', ',')} Stunden gearbeitet — mehr als die zulässigen 10 Stunden (§3 ArbZG).`,
      });
    }
    if (arbeit > 9 * 60 && pause < 45) {
      raus.push({ art: 'pause', tag, text: 'Über 9 Stunden Arbeit, aber weniger als 45 Minuten Pause gestempelt (§4 ArbZG).' });
    } else if (arbeit > 6 * 60 && pause < 30) {
      raus.push({ art: 'pause', tag, text: 'Über 6 Stunden Arbeit, aber weniger als 30 Minuten Pause gestempelt (§4 ArbZG).' });
    }
    const sonntagsarbeit = liste.some((s) => new Date(s.beginn).getDay() === 0);
    if (sonntagsarbeit) {
      raus.push({ art: 'sonntag', tag, text: 'Sonntagsarbeit — nur in den Ausnahmefällen des §10 ArbZG zulässig.' });
    }
  }

  // Ruhezeit: Ende des einen Tages bis Beginn des nächsten.
  const sortiert = [...stempel].filter((s) => s.ende).sort((a, b) => (a.beginn < b.beginn ? -1 : 1));
  for (let i = 1; i < sortiert.length; i++) {
    const vorher = sortiert[i - 1];
    const jetztS = sortiert[i];
    const luecke = dauerMinuten(vorher.ende!, jetztS.beginn, jetzt);
    // Nur zwischen verschiedenen Kalendertagen prüfen (mehrere Abschnitte am selben
    // Tag sind keine Ruhezeit-Unterbrechung).
    if (tagKey(new Date(vorher.beginn)) === tagKey(new Date(jetztS.beginn))) continue;
    if (luecke < RUHEZEIT_MINUTEN) {
      raus.push({
        art: 'ruhezeit', tag: tagKey(new Date(jetztS.beginn)),
        text: `Nur ${Math.floor(luecke / 60)}:${String(luecke % 60).padStart(2, '0')} Stunden Ruhezeit zum Vortag — vorgeschrieben sind 11 (§5 ArbZG).`,
      });
    }
  }

  return raus;
}

/** Zeile für den Lohn-Export (CSV, Semikolon — so erwartet es DATEV/Lodas). */
export function csvZeile(felder: (string | number)[]): string {
  return felder
    .map((f) => {
      const s = typeof f === 'number' ? String(f).replace('.', ',') : String(f ?? '');
      return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    })
    .join(';');
}

// ── Kennzahlen ───────────────────────────────────────────────────────────────

/**
 * Krankheitsquote: Kranktage im Verhältnis zu den Soll-Arbeitstagen.
 * Ohne Soll-Tage (kein Modell hinterlegt) gibt es keine sinnvolle Quote — dann null,
 * statt durch null zu teilen oder 0 % vorzutäuschen.
 */
export function krankheitsquote(kranktage: number, sollArbeitstage: number): number | null {
  if (sollArbeitstage <= 0) return null;
  return Math.round((kranktage / sollArbeitstage) * 1000) / 10;
}

/**
 * Auslastung: wie viel der geleisteten Arbeitszeit ist ABRECHENBAR auf Tickets gebucht.
 * Das ist die betriebswirtschaftlich interessante Zahl — im Unterschied zur „Abdeckung",
 * die jede Ticketzeit zählt, auch die interne.
 */
export function auslastung(abrechenbarMinuten: number, gearbeitetMinuten: number): number | null {
  if (gearbeitetMinuten <= 0) return null;
  return Math.round((abrechenbarMinuten / gearbeitetMinuten) * 1000) / 10;
}
